import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ResponsiveFormGroup } from "@/components/ui/responsive-form";
import { IntakeData } from "../../pages/Index";
import { Loader2, Shield } from "lucide-react";

interface WelcomeStepProps {
  data: IntakeData;
  onUpdate: (data: Partial<IntakeData>) => void;
  onEnsureDraft: (data: Partial<IntakeData>) => Promise<void>;
  onNext: () => void;
}

export default function WelcomeStep({
  data,
  onUpdate,
  onEnsureDraft,
  onNext,
}: WelcomeStepProps) {
  const [formData, setFormData] = useState({
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    phone: data.phone,
    dateOfBirth: data.dateOfBirth,
    city: data.city,
    state: data.state,
    country: data.country,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }

    setErrors(newErrors);

    // Scroll to first error if validation fails
    if (Object.keys(newErrors).length > 0) {
      setTimeout(() => {
        const firstErrorField = Object.keys(newErrors)[0];
        const element = document.getElementById(firstErrorField);

        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          setTimeout(() => {
            element.focus();
          }, 500);
        } else {
          // Fallback: scroll to top of form
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 100);
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = async () => {
    if (isSubmitting) return;
    if (!validateForm()) return;

    setIsSubmitting(true);
    setFormError(null);

    try {
      onUpdate(formData);
      await onEnsureDraft(formData);

      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => {
        onNext();
      }, 100);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to prepare your intake form. Please try again.";
      setFormError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full flex flex-col lg:justify-center">
      {/* Mobile: Traditional layout */}
      <div className="lg:hidden space-y-6">
        {/* Welcome Message */}
        <div className="text-center space-y-4">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto shadow-lg border-4 border-gray-100 overflow-hidden">
            <img
              src="jema services logo only.svg"
              alt="Jema Services Tooth Icon"
              className="w-full h-full object-cover block"
            />
          </div>

          <div>
            <h2 className="heading-secondary text-gray-900 mb-2">
              Welcome to Your <span style={{ color: "#2E86C1" }}>Premium</span>{" "}
              Dental Journey
            </h2>
            <p
              style={{ color: "#F39C12" }}
              className="font-heading font-bold text-lg mb-3"
            >
              Dental Care Without Borders
            </p>
            <div className="bg-gradient-to-r from-[#2E86C1]/10 to-[#F39C12]/10 rounded-lg p-4 mb-4">
              <p className="text-gray-700 text-sm leading-relaxed font-medium">
                <strong>Transform your smile in paradise!</strong> This quick
                5-7 minute process uses 6 precision dental photos with
                professional guidance, enabling our world-class Colombian
                dentists to design your
                <em> perfect personalized treatment plan</em>.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Information Form */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-gray-500" />
            <h3 className="font-heading font-semibold text-gray-900 text-base">
              Verify Your Information
            </h3>
          </div>

          <ResponsiveFormGroup columns={2}>
            <div>
              <Label
                htmlFor="firstName"
                className="text-sm font-medium text-gray-700"
              >
                First Name *
              </Label>
              <Input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
                className={`mt-1 h-12 text-base ${errors.firstName ? "border-red-500" : ""}`}
                placeholder="First name"
              />
              {errors.firstName && (
                <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
              )}
            </div>
            <div>
              <Label
                htmlFor="lastName"
                className="text-sm font-medium text-gray-700"
              >
                Last Name *
              </Label>
              <Input
                id="lastName"
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange("lastName", e.target.value)}
                className={`mt-1 h-12 text-base ${errors.lastName ? "border-red-500" : ""}`}
                placeholder="Last name"
              />
              {errors.lastName && (
                <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
              )}
            </div>
          </ResponsiveFormGroup>

          <div>
            <Label
              htmlFor="email"
              className="text-sm font-medium text-gray-700"
            >
              Email Address *
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              className={`mt-1 h-12 text-base ${errors.email ? "border-red-500" : ""}`}
              placeholder="your@email.com"
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <Label
              htmlFor="phone"
              className="text-sm font-medium text-gray-700"
            >
              Phone Number *
            </Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              className={`mt-1 h-12 text-base ${errors.phone ? "border-red-500" : ""}`}
              placeholder="+1 (555) 123-4567"
            />
            {errors.phone && (
              <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
            )}
          </div>

          {/* Privacy Notice */}
          <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
            <p>
              Your information is encrypted and secure. We only use it for your
              consultation and follow strict privacy guidelines.{" "}
              <a
                href="https://jemaservices.com.co/privacy-policy/"
                className="text-blue-600 underline hover:text-blue-800"
              >
                Privacy Policy
              </a>
            </p>
          </div>

          {/* Continue Button */}
          <div className="space-y-2">
            <Button
              onClick={handleContinue}
              className="w-full shadow-lg hover:shadow-xl transform hover:scale-105"
              variant="jema"
              size="xl"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="animate-spin" />
                  Creating your profile...
                </>
              ) : (
                "Begin Your Transformation"
              )}
            </Button>
            {formError ? (
              <p className="text-red-500 text-sm text-center">{formError}</p>
            ) : null}
          </div>
        </div>
      </div>

      {/* Desktop/Tablet: Modern full-screen layout */}
      <div className="hidden lg:block max-w-2xl xl:max-w-4xl mx-auto">
        <div className="space-y-8 xl:space-y-12">
          {/* Welcome Message - Centered and prominent */}
          <div className="text-center space-y-6">
            <h2 className="heading-primary text-gray-900 leading-tight">
              Welcome to Your <span className="text-[#166B93]">Premium</span>{" "}
              Dental Journey
            </h2>
            <p className="heading-tertiary text-[#FF9142] font-semibold">
              Let's get started with your information
            </p>
          </div>

          {/* Contact Information Form - Modern card-like sections */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 xl:p-10 space-y-8">
            <div className="text-center mb-6">
              <h3 className="heading-tertiary text-gray-900 mb-2">
                Verify Your Information
              </h3>
              <p className="body-base text-gray-600">
                Please confirm your details to get started
              </p>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 xl:gap-8">
              <div>
                <Label
                  htmlFor="firstName"
                  className="text-base font-medium text-gray-700 mb-2 block"
                >
                  First Name *
                </Label>
                <Input
                  id="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                  className={`h-14 text-base rounded-xl border-2 focus:border-[#166B93] transition-colors ${errors.firstName ? "border-red-500" : "border-gray-200"}`}
                  placeholder="Enter your first name"
                />
                {errors.firstName && (
                  <p className="text-red-500 text-sm mt-2">
                    {errors.firstName}
                  </p>
                )}
              </div>
              <div>
                <Label
                  htmlFor="lastName"
                  className="text-base font-medium text-gray-700 mb-2 block"
                >
                  Last Name *
                </Label>
                <Input
                  id="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                  className={`h-14 text-base rounded-xl border-2 focus:border-[#166B93] transition-colors ${errors.lastName ? "border-red-500" : "border-gray-200"}`}
                  placeholder="Enter your last name"
                />
                {errors.lastName && (
                  <p className="text-red-500 text-sm mt-2">{errors.lastName}</p>
                )}
              </div>
              <div>
                <Label
                  htmlFor="email"
                  className="text-base font-medium text-gray-700 mb-2 block"
                >
                  Email Address *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className={`h-14 text-base rounded-xl border-2 focus:border-[#166B93] transition-colors ${errors.email ? "border-red-500" : "border-gray-200"}`}
                  placeholder="your@email.com"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-2">{errors.email}</p>
                )}
              </div>
              <div>
                <Label
                  htmlFor="phone"
                  className="text-base font-medium text-gray-700 mb-2 block"
                >
                  Phone Number *
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  className={`h-14 text-base rounded-xl border-2 focus:border-[#166B93] transition-colors ${errors.phone ? "border-red-500" : "border-gray-200"}`}
                  placeholder="+1 (555) 123-4567"
                />
                {errors.phone && (
                  <p className="text-red-500 text-sm mt-2">{errors.phone}</p>
                )}
              </div>
            </div>

            {/* Privacy Notice */}
            <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-gray-500" />
                <span className="font-medium">Your data is secure</span>
              </div>
              <p>
                Your information is encrypted and secure. We only use it for
                your consultation and follow strict privacy guidelines.{" "}
                <a
                  href="https://jemaservices.com.co/privacy-policy/"
                  className="text-[#166B93] underline hover:text-[#166B93]/80"
                >
                  Privacy Policy
                </a>
              </p>
            </div>

            {/* Continue Button */}
            <div className="space-y-2">
              <Button
                onClick={handleContinue}
                className="w-full shadow-lg hover:shadow-xl transform hover:scale-105"
                variant="jema"
                size="xl"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin" />
                    Creating your profile...
                  </>
                ) : (
                  "Begin Your Transformation Journey"
                )}
              </Button>
              {formError ? (
                <p className="text-red-500 text-sm text-center">{formError}</p>
              ) : null}
            </div>
          </div>

          {/* Features Preview - Desktop only */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-12">
            <div className="text-center p-6 bg-gradient-to-br from-[#166B93]/5 to-[#166B93]/10 rounded-2xl">
              <div className="w-16 h-16 bg-[#166B93] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-xl font-bold">1</span>
              </div>
              <h4 className="font-heading font-semibold text-gray-900 mb-2">
                Health Assessment
              </h4>
              <p className="body-small text-gray-600">
                Comprehensive medical & travel questionnaire
              </p>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-[#CEF12F]/5 to-[#CEF12F]/10 rounded-2xl">
              <div className="w-16 h-16 bg-[#CEF12F] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gray-900 text-xl font-bold">2</span>
              </div>
              <h4 className="font-heading font-semibold text-gray-900 mb-2">
                Photo Capture
              </h4>
              <p className="body-small text-gray-600">
                6 precision dental photos with professional guidance
              </p>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-[#FF9142]/5 to-[#FF9142]/10 rounded-2xl">
              <div className="w-16 h-16 bg-[#FF9142] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-xl font-bold">3</span>
              </div>
              <h4 className="font-heading font-semibold text-gray-900 mb-2">
                Treatment Plan
              </h4>
              <p className="body-small text-gray-600">
                Expert analysis + Colombian tourism packages
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
